import dayjs from 'dayjs';
import type { FlowByPhaseDataOverTime } from '../../../../api/work-periods-client/work-periods-client.type';
import type { StatusLabelResolver } from '../../../../helpers/status-labels/status-labels.type';
import {
  extractPhaseKeys,
  normalizeStatusName,
  sortPhaseKeysByWorkflow,
} from '../../../../helpers/subgroup-helpers/subgroup-helpers';
import { Metric } from '../../strategy.type';
import { PHASES } from './flow-by-phase-tab.constants';
import type { ChartDataItem } from './main-section/flow-by-phase-chart/flow-by-phase-chart.type';
import type { PhaseMetadataItem } from './flow-by-phase-tab.type';

/**
 * Creates an object with all phases set to visible (true).
 *
 * @returns {Record<string, boolean>} An object with phase names as keys and boolean values (all true)
 */
const getInitialVisiblePhases = (): Record<string, boolean> => {
  return Object.fromEntries(PHASES.map((phase) => [phase, true]));
};

/**
 * Finds the most recent date in the Flow by Phase data that has actual data.
 * A date is considered to have data if any phase has a non-null value greater than 0.
 *
 * @param {FlowByPhaseDataOverTime} data - The Flow by Phase data over time
 * @returns {string | null} The latest date with data in ISO format, or null if no dates with data are found
 */
const getLatestDateWithData = (data: FlowByPhaseDataOverTime): string | null => {
  try {
    const dates = Object.entries(data)
      .filter(([_, values]) => {
        if (!values || typeof values !== 'object') return false;

        return Object.entries(values).some(([_, value]) => {
          return value !== null && typeof value === 'number' && value > 0;
        });
      })
      .map(([date]) => date)
      .sort((a, b) => dayjs(b).diff(dayjs(a)));

    return dates.length > 0 ? dates[0] : null;
  } catch (error) {
    console.error('Error finding latest date with data:', error);
    return null;
  }
};

/**
 * Formats flow by phase chart data for Flora AI analysis.
 *
 * @param {ChartDataItem[]} chartData - The chart data to format
 * @param {Metric} metric - The metric type (Tasks or Points)
 * @param {PhaseMetadataItem[]} phaseMetadata - Metadata for phases including labels
 * @returns {Record<string, Record<string, number | null>>} Formatted data with dates as keys and phase labels with their values
 */
const formatFlowByPhaseDataForFlora = (
  chartData: ChartDataItem[],
  metric: Metric,
  phaseMetadata: PhaseMetadataItem[],
): Record<string, Record<string, number | null>> => {
  const formattedData: Record<string, Record<string, number | null>> = {};

  chartData.forEach((item) => {
    const dateData: Record<string, number | null> = {};

    phaseMetadata.forEach((phase) => {
      const key = metric === Metric.Tasks ? `tasks_${phase.id}` : `points_${phase.id}`;
      const value = item[key];
      dateData[phase.label] = typeof value === 'number' ? value : null;
    });

    formattedData[item.date] = dateData;
  });

  return formattedData;
};

/**
 * Moves statuses that the workflow order does not know about to the front of
 * the list, preserving relative order within each group.
 *
 * `sortPhaseKeysByWorkflow` appends unknown keys last, which is right for a
 * left-to-right legend but wrong for a stack: the chart reverses the list so
 * the end of the process sits at the bottom, and an appended unknown key would
 * take that slot. A status the workflow has not mapped yet — routine when a
 * team adds a Jira status before the workflow map catches up — is treated as
 * start-of-process instead.
 *
 * @param {string[]} keys - Keys already sorted by `sortPhaseKeysByWorkflow`.
 * @param {string[]} orderedStatuses - The workflow order those keys were sorted
 *   against; an empty list means nothing is known, so the input is returned
 *   unchanged.
 * @returns {string[]} Unknown keys first, then the workflow-ordered keys.
 */
const moveUnmappedKeysToFront = (keys: string[], orderedStatuses: string[]): string[] => {
  if (orderedStatuses.length === 0) {
    return keys;
  }

  const known = new Set(orderedStatuses.map(normalizeStatusName));
  const mapped: string[] = [];
  const unmapped: string[] = [];

  for (const key of keys) {
    (known.has(normalizeStatusName(key)) ? mapped : unmapped).push(key);
  }

  return [...unmapped, ...mapped];
};

/**
 * Create phase metadata for subgroups
 *
 * `id` and `description` stay the raw phase key — they index the color map and
 * the chart's series — while `label` carries the board's source-cased status
 * name for display.
 *
 * @param {FlowByPhaseDataOverTime} [data] - The flow by phase data
 * @param {Record<string, string>} colorMap - Color map for subgroups
 * @param {StatusLabelResolver} [resolveStatusLabelForDisplay] - Maps a raw phase key to
 *   its source-cased status label; omit to display raw keys
 * @param {string[]} [orderedStatuses] - Status names in process order, used to
 *   sort the discovered keys. Omit (or pass an empty array) to keep the raw
 *   API discovery order, which is JSON key-insertion order rather than
 *   workflow order. Keys absent from this list are placed FIRST, not last:
 *   consumers reverse the result for stacking, so appending them (as
 *   `sortPhaseKeysByWorkflow` does) would make an unmapped status the bottom
 *   band and displace the end-of-process phase.
 * @returns {Array} Array of phase metadata items
 *
 * @example
 * const metadata = createSubGroupPhaseMetadata(flowData, colorMap, getStatusLabel);
 * // [{ id: 'in progress', label: 'In Progress', description: 'in progress', color: '#...' }]
 * // `id` stays raw so it keeps matching the chart series and the color map.
 */
const createSubGroupPhaseMetadata = (
  data: FlowByPhaseDataOverTime | null,
  colorMap: Record<string, string>,
  resolveStatusLabelForDisplay?: StatusLabelResolver,
  orderedStatuses?: string[],
): Array<{ id: string; label: string; description: string; color: string }> => {
  if (!data) {
    return [];
  }

  const phaseKeys = moveUnmappedKeysToFront(
    sortPhaseKeysByWorkflow(extractPhaseKeys(data), orderedStatuses ?? []),
    orderedStatuses ?? [],
  );
  return phaseKeys.map((key) => ({
    id: key,
    label: resolveStatusLabelForDisplay ? resolveStatusLabelForDisplay(key) : key,
    description: key,
    color: colorMap[key],
  }));
};

export { getInitialVisiblePhases, getLatestDateWithData, formatFlowByPhaseDataForFlora, createSubGroupPhaseMetadata };
